import './index.css'
import clsx from 'clsx'

import React, { useCallback, useEffect, useState } from 'react'
import { Spinner } from 'components/ds'
import Icon from 'components/ui/icon'
type Props = {
  arrowPosition?: 'left'
  children: React.ReactNode
  loading?: boolean
  open?: boolean
  title?: string
  actionButton?: React.ReactNode
  color?: 'gray' | 'dark'
  border?: 'bottom'
  nodeLeft?: React.ReactNode
  onToggle?: (open: boolean) => void
}

/**
 * A compact accordion component with customizable styling and optional loading state
 * @param arrowPosition - Position of the arrow indicator, currently only supports 'left'
 * @param children - Content to be shown/hidden when accordion is expanded
 * @param loading - Whether to show a loading spinner in the title - defaults to false
 * @param open - Whether the accordion is initially open - defaults to false
 * @param title - The title text displayed in the accordion header
 * @param actionButton - Optional button element to display in the header
 * @param color - Color theme for the accordion header ('gray' or 'dark')
 * @param border - Border style, currently only supports 'bottom'
 * @param nodeLeft - Optional element to display on the left side of the title
 * @param onToggle - Callback function called when accordion is toggled, receives new open state
 * @returns React fragment containing the accordion header and content
 */
const CompactAccordion = ({
  arrowPosition,
  children,
  loading,
  open = false,
  title,
  actionButton,
  color,
  border,
  nodeLeft,
  onToggle,
}: Props): React.ReactNode => {
  const [isOpen, setIsOpen] = useState<boolean>(open)
  useEffect(() => {
    setIsOpen(open)
  }, [open])
  const handleToggle = useCallback(() => {
    setIsOpen((open: boolean) => {
      if (onToggle) {
        onToggle(!open)
      }

      return !open
    })
  }, [onToggle])
  return (
    <>
      {title && (
        <h2
          className={clsx(
            'CompactAccordion__TitleContainer',
            {
              'CompactAccordion__TitleContainer--Gray': color === 'gray',
              'CompactAccordion__TitleContainer--Dark': color === 'dark',
            },
            {
              ' CompactAccordion__TitleContainer--HideBorderTop':
                border === 'bottom',
            },
            {
              ' CompactAccordion__TitleContainer--NodeLeft': nodeLeft,
            },
          )}
          onClick={handleToggle}
        >
          {nodeLeft && (
            <span className="CompactAccordion__NodeLeft">{nodeLeft}</span>
          )}
          <span className="CompactAccordion__Title">
            <span>{title}</span>
            {loading && <Spinner />}
          </span>
          <Icon
            className={clsx('CompactAccordion__Arrow', {
              'CompactAccordion__Arrow--Open': isOpen,
              'CompactAccordion__Arrow--Left': arrowPosition === 'left',
            })}
            type="arrow-right-compact"
            width={16}
            height={16}
          />
          {Boolean(actionButton) && (
            <div className="CompactAccordion__Button">{actionButton}</div>
          )}
        </h2>
      )}

      {isOpen && children}
    </>
  )
}

export default CompactAccordion
